// File: /src/hooks/clients/useClientSelection.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const getInitialViewMode = () => {
	if (typeof window === 'undefined') {
		return 'grid';
	}
	return window.localStorage.getItem('gridClientsViewMode') || 'grid';
};

const filterByArchiveState = (items = [], showArchived) =>
	items.filter((item) =>
		showArchived ? Boolean(item.archived) : !item.archived,
	);

export const useClientSelection = ({
	clients = [],
	services = [],
	invoices = [],
	selectedClients = [],
	setSelectedClients,
	toggleSelectClient,
	modalFunctions,
	refreshClients,
	refreshProjects,
	refreshInvoices,
}) => {
	const [selectedProjects, setSelectedProjects] = useState([]);
	const [selectedInvoices, setSelectedInvoices] = useState([]);
	const [viewMode, setViewMode] = useState(getInitialViewMode);
	const [showArchived, setShowArchived] = useState(false);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			window.localStorage.setItem('gridClientsViewMode', viewMode);
		}
	}, [viewMode]);

	const handleChangeView = useCallback((mode) => {
		setViewMode(mode);
	}, []);

	const toggleSelectProject = useCallback((projectId) => {
		setSelectedProjects((prevSelected) =>
			prevSelected.includes(projectId)
				? prevSelected.filter((id) => id !== projectId)
				: [...prevSelected, projectId],
		);
	}, []);

	const toggleSelectInvoice = useCallback((invoiceId) => {
		setSelectedInvoices((prevSelected) =>
			prevSelected.includes(invoiceId)
				? prevSelected.filter((id) => id !== invoiceId)
				: [...prevSelected, invoiceId],
		);
	}, []);

	const filteredClients = useMemo(
		() => filterByArchiveState(clients, showArchived),
		[clients, showArchived],
	);

	const filteredServices = useMemo(
		() => filterByArchiveState(services, showArchived),
		[services, showArchived],
	);

	const filteredInvoices = useMemo(
		() => filterByArchiveState(invoices, showArchived),
		[invoices, showArchived],
	);

	const resetSelections = useCallback(() => {
		setSelectedClients?.([]);
		setSelectedProjects([]);
		setSelectedInvoices([]);
	}, [setSelectedClients]);

	const refreshAll = useCallback(async () => {
		await Promise.all([
			refreshClients?.(),
			refreshProjects?.(),
			refreshInvoices?.(),
		]);
	}, [refreshClients, refreshProjects, refreshInvoices]);

	const archiveItems = useCallback(
		async (shouldArchive) => {
			if (!modalFunctions) {
				toast.error('Funções de arquivamento indisponíveis.');
				return;
			}

			const actions = [];

			selectedClients.forEach((clientId) => {
				actions.push(
					modalFunctions.handleArchiveClient(clientId, shouldArchive),
				);
			});

			selectedProjects.forEach((projectId) => {
				actions.push(
					modalFunctions.handleArchiveProject(
						projectId,
						shouldArchive,
					),
				);
			});

			selectedInvoices.forEach((invoiceId) => {
				actions.push(
					modalFunctions.handleArchiveInvoice(
						invoiceId,
						shouldArchive,
					),
				);
			});

			if (actions.length === 0) {
				toast.error('Selecione pelo menos um item para continuar.');
				return;
			}

			try {
				await Promise.all(actions);
				resetSelections();
				await refreshAll();
				toast.success(
					shouldArchive
						? 'Itens arquivados com sucesso!'
						: 'Itens desarquivados com sucesso!',
				);
			} catch (error) {
				console.error('Erro ao atualizar arquivamento:', error);
				toast.error('Não foi possível atualizar o arquivamento.');
			}
		},
		[
			modalFunctions,
			refreshAll,
			resetSelections,
			selectedClients,
			selectedProjects,
			selectedInvoices,
		],
	);

	const handleArchiveSelected = useCallback(
		() => archiveItems(true),
		[archiveItems],
	);

	const handleUnarchiveSelected = useCallback(
		() => archiveItems(false),
		[archiveItems],
	);

	const totalSelectedItems = useMemo(
		() =>
			selectedClients.length +
			selectedProjects.length +
			selectedInvoices.length,
		[
			selectedClients.length,
			selectedProjects.length,
			selectedInvoices.length,
		],
	);

	return {
		viewMode,
		handleChangeView,
		showArchived,
		setShowArchived,
		filteredClients,
		filteredServices,
		filteredInvoices,
		selectedProjects,
		selectedInvoices,
		toggleSelectProject,
		toggleSelectInvoice,
		selectedClients,
		setSelectedClients,
		toggleSelectClient,
		handleArchiveSelected,
		handleUnarchiveSelected,
		totalSelectedItems,
		resetSelections,
	};
};

export default useClientSelection;
